import React, { useState, useMemo, useEffect } from "react"
import { Button, Flex } from "antd"
import { useTitle } from "hooks/useTitle"
import { useTranslation } from "react-i18next"
import { PlusOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import styles from "./staff.module.css"
import StaffTable from "./StaffTable"
import SearchInput from "ui/SearchInput"
import RolesAccess from "./RolesAccess"
import AddStaffModal from "./AddStaffModal"
import Tabs from "ui/Tabs"
import { debounce } from "lodash"
import {
  getStaffMembers,
  selectStaffMembers,
  selectStaffMembersLoading,
  selectStaffMembersMetadata,
  getRoles,
  selectRoles
} from "store/staff"
import CustomPagination from "components/common/CustomPagination"
import CategorySelect from "components/common/CategorySelect"
import FilterChips from "ui/FilterChips"

const DELAY_TIME = 300

const Staff = () => {
  useTitle("Staff")
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const staffMembers = useSelector(selectStaffMembers)
  const staffLoading = useSelector(selectStaffMembersLoading)
  const metadata = useSelector(selectStaffMembersMetadata)
  const roles = useSelector(selectRoles)

  const [activeTab, setActiveTab] = useState("staff")
  const [searchInput, setSearchInput] = useState("")
  const [searchBy, setSearchBy] = useState("")
  const [selectedRoles, setSelectedRoles] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false)
  const [editingStaffId, setEditingStaffId] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortBy, setSortBy] = useState("")
  const [sortDirection, setSortDirection] = useState("asc")

  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  useEffect(() => {
    const params = {
      page: currentPage,
      per_page: pageSize
    }

    if (selectedStatuses.length > 0 && !selectedStatuses.includes("all")) {
      if (selectedStatuses.length === 1) {
        params.status = selectedStatuses[0]
      } else {
        params["status[]"] = selectedStatuses
      }
    }

    if (selectedRoles.length > 0 && !selectedRoles.includes("all")) {
      params["role_ids[]"] = selectedRoles
    }

    if (searchBy.trim()) {
      params.search = searchBy.trim()
    }

    if (sortBy) {
      params.sort_by = sortBy
      params.sort_direction = sortDirection
    }

    dispatch(getStaffMembers(params))
  }, [dispatch, currentPage, pageSize, selectedRoles, selectedStatuses, searchBy, sortBy, sortDirection])

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchBy(value)
        setCurrentPage(1)
      }, DELAY_TIME),
    []
  )

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  const handleSearchChange = (value) => {
    setSearchInput(value)
    if (!value || value.trim() === "") {
      setSearchBy("")
      setCurrentPage(1)
    } else {
      debouncedSetSearch(value)
    }
  }

  const handleRolesChange = (roleIds) => {
    setSelectedRoles(roleIds)
    setCurrentPage(1)
  }

  const handleStatusesChange = (statuses) => {
    setSelectedStatuses(statuses)
    setCurrentPage(1)
  }

  const handleAddStaff = () => {
    setIsStaffModalOpen(true)
  }

  const handleEditStaff = (staffId) => {
    setEditingStaffId(staffId)
    setIsStaffModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsStaffModalOpen(false)
    setEditingStaffId(null)
  }

  const handleSaveStaff = async () => {
    const params = {
      page: currentPage,
      per_page: pageSize
    }

    if (selectedStatuses.length > 0 && !selectedStatuses.includes("all")) {
      if (selectedStatuses.length === 1) {
        params.status = selectedStatuses[0]
      } else {
        params["status[]"] = selectedStatuses
      }
    }

    if (selectedRoles.length > 0 && !selectedRoles.includes("all")) {
      params["role_ids[]"] = selectedRoles
    }

    if (searchBy.trim()) {
      params.search = searchBy.trim()
    }

    if (sortBy) {
      params.sort_by = sortBy
      params.sort_direction = sortDirection
    }

    await dispatch(getStaffMembers(params))
    setIsStaffModalOpen(false)
    setEditingStaffId(null)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (size) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const handleTableChange = (sorter) => {
    if (sorter && sorter.columnKey) {
      const sortField = sorter.columnKey
      const sortOrder = sorter.order

      if (!sortOrder) {
        setSortBy("")
        setSortDirection("asc")
        setCurrentPage(1)
      } else {
        setSortBy(sortField)
        setSortDirection(sortOrder === "ascend" ? "asc" : "desc")
        setCurrentPage(1)
      }
    } else {
      setSortBy("")
      setSortDirection("asc")
      setCurrentPage(1)
    }
  }

  const roleOptions = useMemo(() => {
    if (!roles || roles.length === 0) return []

    return [
      { value: "all", label: t("staff.filters.all_roles") },
      ...roles.map((role) => ({
        value: role.id,
        label: role.name
      }))
    ]
  }, [roles, t])

  const statusOptions = useMemo(
    () => [
      { value: "all", label: t("staff.filters.all_statuses") },
      { value: "active", label: t("staff.status.active") },
      { value: "deactivated", label: t("staff.status.deactivated") }
    ],
    [t]
  )

  const tabItems = useMemo(
    () => [
      { key: "staff", label: t("staff.tabs.staff") },
      { key: "roles", label: t("staff.tabs.roles_access") }
    ],
    [t]
  )

  return (
    <>
      <div className={styles.customTabs}>
        <Tabs tabs={tabItems} activeTab={activeTab} onTabChange={setActiveTab} />
        <div className={styles.tabContent}>
          {activeTab === "staff" && (
            <>
              <Flex justify="space-between" wrap="wrap" gap={10} className={`mb-xl ${styles.filterWrap}`}>
                <Flex gap={16} wrap align="center" className={`flex-1 ${styles.searchAndFilters}`}>
                  <SearchInput
                    searchBy={searchInput}
                    setSearchBy={handleSearchChange}
                    placeholder={t("staff.search_staff")}
                    ariaLabel={t("staff.search_staff")}
                    ariaControls="staff-search-results"
                    className={styles.searchInput}
                  />
                  <CategorySelect
                    value={selectedRoles}
                    onChange={handleRolesChange}
                    options={roleOptions}
                    placeholder={t("staff.filters.role")}
                  />
                  <CategorySelect
                    value={selectedStatuses}
                    onChange={handleStatusesChange}
                    options={statusOptions}
                    placeholder={t("staff.filters.status")}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddStaff}
                    aria-label={t("staff.add_staff")}
                    size="large"
                    icon={<PlusOutlined aria-hidden="true" />}
                    className={styles.addStaffButton}
                  >
                    {t("staff.add_staff")}
                  </Button>
                </Flex>
              </Flex>
              <Flex gap={16} wrap className="mt-s">
                <FilterChips selectedValues={selectedRoles} options={roleOptions} onChange={handleRolesChange} />
                <FilterChips selectedValues={selectedStatuses} options={statusOptions} onChange={handleStatusesChange} />
              </Flex>
              <div className="commonGrid">
                <StaffTable
                  staff={staffMembers}
                  loading={staffLoading}
                  sortField={sortBy}
                  sortOrder={sortBy ? (sortDirection === "asc" ? "ascend" : "descend") : null}
                  handleTableChange={handleTableChange}
                  onEdit={handleEditStaff}
                />
                {staffMembers.length > 0 && metadata && (
                  <CustomPagination
                    metadata={metadata}
                    pageSize={pageSize}
                    total={metadata.total_results || 0}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                )}
              </div>
            </>
          )}
          {activeTab === "roles" && <RolesAccess />}
        </div>
      </div>
      <AddStaffModal
        open={isStaffModalOpen}
        onCancel={handleCloseModal}
        onSave={handleSaveStaff}
        staffId={editingStaffId}
        editMode={!!editingStaffId}
      />
    </>
  )
}

export default Staff
